import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Table, Pagination, Select } from 'components/ui'
import TableRowSkeleton from './loaders/TableRowSkeleton'
import Loading from './Loading'
import { useTable, usePagination, useSortBy } from 'react-table'

const { Tr, Th, Td, THead, TBody, Sorter } = Table

function ResizableDataTable(props) {
  const {
    skeletonAvatarColumns,
    columns: originalColumns,
    data,
    loading,
    onPaginationChange,
    onSelectChange,
    onSort,
    pageSizes,
    skeletonAvatarProps,
    pagingData,
    pagination,
  } = props;

  const { pageSize, pageIndex, total } = pagingData;

  const pageSizeOption = useMemo(() =>
    pageSizes.map((number) => ({ value: number, label: `${number} / page` }))
  , [pageSizes]);

  const columns = useMemo(() => {
    return originalColumns.map((col) => ({
      ...col,
      minWidth: col?.minWidth || 30,
    }));
  }, [originalColumns]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
  } = useTable(
    {
      columns,
      data,
      enableColumnResizing: true,
      manualPagination: true,
      manualSortBy: true,
      autoResetSelectedRows: false,
      autoResetSelectedCell: false,
      autoResetSelectedColumn: false,
    },
    useSortBy,
    usePagination,
    (hooks) => {
      hooks.visibleColumns.push((cols) => [
        ...cols,
      ]);
    }
  );

  const handlePaginationChange = (page) => {
    if (!loading) {
      onPaginationChange?.(page);
    }
  };

  const handleSelectChange = (value) => {
    if (!loading) {
      onSelectChange?.(Number(value));
    }
  };

  const handleSort = (column) => {
    if (!loading) {
      const { id, isSortedDesc, toggleSortBy, clearSortBy } = column;
      const sortOrder = isSortedDesc ? 'desc' : 'asc';
      toggleSortBy(!isSortedDesc);
      onSort?.({ order: sortOrder, key: id }, { id, clearSortBy });
    }
  };

  return (
    <Loading loading={loading && data.length !== 0} type="cover">
      <Table {...getTableProps()}>
        <THead>
          {headerGroups.map((headerGroup) => (
            <Tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <Th {...column.getHeaderProps()}>
                  {column.render('Header') && (
                    column.sortable ? (
                      <div
                        className="table-resizer cursor-all-scroll isResizing"
                        style={{ minWidth: column.minWidth }}
                        onClick={() => handleSort(column)}
                      >
                        {column.render('Header')}
                        <span>
                          <Sorter sort={column.isSortedDesc}/>
                        </span>
                      </div>
                    ) : (
                      <div style={{ minWidth: column.minWidth }}>{column.render('Header')}</div>
                    )
                  )}
                </Th>
              ))}
            </Tr>
          ))}
        </THead>
        {loading && data.length === 0 ? (
          <TableRowSkeleton
            columns={columns.length}
            rows={pagingData.pageSize}
            avatarInColumns={skeletonAvatarColumns}
            avatarProps={skeletonAvatarProps}
          />
        ) : (
          <TBody {...getTableBodyProps()}>
            {page.map((row, i) => {
              prepareRow(row);
              return (
                <Tr {...row.getRowProps()}>
                  {row.cells.map((cell) => (
                    <Td {...cell.getCellProps()}>{cell.render('Cell')}</Td>
                  ))}
                </Tr>
              );
            })}
          </TBody>
        )}
      </Table>
      {pagination && (
        <div className="flex items-center justify-between mt-4">
          <Pagination
            pageSize={pageSize}
            currentPage={pageIndex}
            total={total}
            onChange={handlePaginationChange}
          />
          <div style={{ minWidth: 130 }}>
            <Select
              size="sm"
              menuPlacement="top"
              isSearchable={false}
              value={pageSizeOption.filter((option) => option.value === pageSize)}
              options={pageSizeOption}
              onChange={(option) => handleSelectChange(option.value)}
            />
          </div>
        </div>
      )}
    </Loading>
  );
}

ResizableDataTable.propTypes = {
	columns: PropTypes.array,
	data: PropTypes.array,
	loading: PropTypes.bool,
	onCheckBoxChange: PropTypes.func,
	onIndeterminateCheckBoxChange: PropTypes.func,
	onPaginationChange: PropTypes.func,
	onSelectChange: PropTypes.func,
	onSort: PropTypes.func,
	pageSizes: PropTypes.arrayOf(PropTypes.number),
	selectable: PropTypes.bool,
	skeletonAvatarColumns: PropTypes.arrayOf(PropTypes.number),
	skeletonAvatarProps: PropTypes.object,
	pagingData: PropTypes.shape({
		total: PropTypes.number,
		pageIndex: PropTypes.number,
		pageSize: PropTypes.number,
	}),
	allCheckbox: PropTypes.bool,
	enableColumnResizing: PropTypes.bool
};

ResizableDataTable.defaultProps = {
	pagination: false,
	pageSizes: [10, 25, 50, 100],
	pagingData: {
		total: 0,
		pageIndex: 1,
		pageSize: 10,
	},
	data: [],
	columns: [],
	selectable: false,
	loading: false,
	allCheckbox: false,
	enableColumnResizing: false
};

export default ResizableDataTable;
